import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { articles } from '@/mocks/articles';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import ScrollToTop from '@/components/feature/ScrollToTop';

const categoryLabels: Record<string, string> = {
  football: '足球',
  basketball: '篮球',
  tennis: '网球',
  esports: '电竞',
  comprehensive: '综合体育',
};

const articleImages: Record<number, string> = {
  1: 'https://readdy.ai/api/search-image?query=Chinese%20national%20soccer%20team%20in%20World%20Cup%20qualifier%20match%2C%20players%20in%20red%20jerseys%20celebrating%2C%20dramatic%20stadium%20lighting%2C%20professional%20sports%20photography%2C%20editorial%20quality&width=1200&height=630&seq=detail-football-1&orientation=landscape',
  2: 'https://readdy.ai/api/search-image?query=Lakers%20basketball%20team%20in%20NBA%20game%20action%2C%20star%20player%20dunking%2C%20golden%20jerseys%2C%20intense%20arena%20atmosphere%2C%20professional%20sports%20photography&width=1200&height=630&seq=detail-basketball-1&orientation=landscape',
  3: 'https://readdy.ai/api/search-image?query=Wimbledon%20center%20court%20with%20two%20players%20shaking%20hands%20after%20epic%20final%2C%20golden%20trophy%20in%20background%2C%20prestigious%20tennis%20moment&width=1200&height=630&seq=detail-tennis-1&orientation=landscape',
  4: 'https://readdy.ai/api/search-image?query=League%20of%20Legends%20world%20championship%20summoners%20cup%20trophy%2C%20grand%20esports%20stage%20with%20fireworks%2C%20celebratory%20atmosphere%2C%20epic%20gaming%20moment&width=1200&height=630&seq=detail-esports-1&orientation=landscape',
  5: 'https://readdy.ai/api/search-image?query=Chinese%20table%20tennis%20players%20holding%20five%20gold%20medals%20at%20world%20championships%2C%20national%20team%20celebration%2C%20proud%20moment&width=1200&height=630&seq=detail-comp-1&orientation=landscape',
  6: 'https://readdy.ai/api/search-image?query=Manchester%20City%20versus%20Liverpool%20Premier%20League%20opening%20match%2C%20intense%20soccer%20rivalry%2C%20packed%20stadium%2C%20cinematic%20sports%20photography&width=1200&height=630&seq=detail-football-2&orientation=landscape',
  7: 'https://readdy.ai/api/search-image?query=CBA%20Chinese%20basketball%20league%20summer%20transfer%20press%20conference%2C%20new%20player%20jersey%20reveal%2C%20professional%20sports%20setting&width=1200&height=630&seq=detail-basketball-2&orientation=landscape',
  8: 'https://readdy.ai/api/search-image?query=Formula%201%20race%20car%20on%20Hungaroring%20track%20in%20rain%2C%20water%20spray%20behind%2C%20dramatic%20motorsport%20cinematic%20photography%2C%20speed%20and%20motion&width=1200&height=630&seq=detail-comp-2&orientation=landscape',
  9: 'https://readdy.ai/api/search-image?query=DOTA2%20The%20International%20main%20stage%20with%20Chinese%20team%2C%20giant%20screens%2C%20epic%20arena%2C%20thousands%20of%20fans%2C%20professional%20esports%20event&width=1200&height=630&seq=detail-esports-2&orientation=landscape',
  10: 'https://readdy.ai/api/search-image?query=Young%20female%20tennis%20champion%20kissing%20French%20Open%20trophy%20at%20Roland%20Garros%2C%20red%20clay%20court%2C%20emotional%20victory%20moment%2C%20golden%20sunset&width=1200&height=630&seq=detail-tennis-2&orientation=landscape',
  11: 'https://readdy.ai/api/search-image?query=Champions%20League%20draw%20ceremony%20in%20Monaco%2C%20football%20club%20balls%20being%20drawn%2C%20elegant%20venue%2C%20prestigious%20soccer%20event&width=1200&height=630&seq=detail-football-3&orientation=landscape',
  12: 'https://readdy.ai/api/search-image?query=Chinese%20swimming%20team%20arriving%20at%20Budapest%20world%20championships%2C%20athletes%20in%20tracksuits%2C%20Olympic%20pool%20background%2C%20professional%20sports&width=1200&height=630&seq=detail-comp-3&orientation=landscape',
};

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();

  const article = articles.find((a) => a.id === Number(id));

  if (!article) {
    return (
      <>
        <SEOHead title="文章未找到 - 开云体育" />
        <ScrollToTop />
        <Navbar />
        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
            <p className="text-foreground-600">文章未找到</p>
            <Link to="/" className="text-primary-600 hover:text-primary-700 mt-4 inline-block">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  const categoryName = categoryLabels[article.category] || article.category;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: '开云体育',
      url: 'https://www.kaiyunsports.com',
    },
    datePublished: article.publishedAt,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.kaiyunsports.com/article/${article.id}`,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'https://www.kaiyunsports.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: categoryName,
        item: `https://www.kaiyunsports.com/category/${article.category}`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: article.title,
      },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${article.title} - 开云体育`}
        description={article.summary}
        keywords={`开云体育,${categoryName},${article.tags.join(',')}`}
        canonicalPath={`/article/${article.id}`}
        ogTitle={article.title}
        ogDescription={article.summary}
        ogImage={articleImages[article.id]}
        schema={[articleSchema, breadcrumbSchema]}
      />
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <article className="bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6 py-8">
            <Breadcrumb
              items={[
                { label: categoryName, to: `/category/${article.category}` },
                { label: article.title },
              ]}
            />

            <header className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <span className="text-xs font-medium bg-primary-100 text-primary-600 px-2.5 py-1 rounded">
                  {categoryName}
                </span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs text-foreground-500 bg-background-100 px-2 py-1 rounded"
                  >
                    {tag}
                  </span>
                ))}
              </div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold font-heading text-foreground-950 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i>
                  {t('article.author')}：{article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-links-line"></i>
                  {t('article.source')}：{article.source}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {t('article.published')}：{article.publishedAt}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {article.readCount >= 10000 ? `${(article.readCount / 10000).toFixed(1)}万` : article.readCount} 阅读
                </span>
              </div>
            </header>

            <div className="mb-10 rounded-lg overflow-hidden">
              <img
                src={articleImages[article.id]}
                alt={article.title}
                title={article.title}
                className="w-full h-auto object-cover"
              />
            </div>

            <div
              className="prose prose-stone max-w-none mb-12 text-foreground-800 leading-relaxed text-sm md:text-base"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="border-t border-background-200/70 pt-6 mb-12">
              <div className="flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs bg-background-100 text-foreground-600 px-3 py-1.5 rounded-full"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>

            <div className="flex justify-between items-center mb-12">
              <Link
                to={`/category/${article.category}`}
                className="flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-600 transition-colors"
              >
                <i className="ri-arrow-left-line"></i>
                {t('article.back')}
              </Link>
            </div>

            {relatedArticles.length > 0 && (
              <section className="border-t border-background-200/70 pt-10">
                <h3 className="text-lg font-bold font-heading text-foreground-950 mb-6">
                  {t('article.related')}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-5">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.id}
                      to={`/article/${ra.id}`}
                      className="group bg-background-100 rounded-lg overflow-hidden border border-background-200/60 transition-all duration-300 hover:scale-[1.01] cursor-pointer"
                    >
                      <div className="relative w-full h-36 overflow-hidden">
                        <img
                          src={articleImages[ra.id] || 'https://readdy.ai/api/search-image?query=Generic%20sports%20photography%20scene%20with%20dynamic%20athletic%20action%2C%20professional%20sports%20lighting%2C%20clean%20editorial%20style&width=800&height=450&seq=related-fallback&orientation=landscape'}
                          alt={ra.title}
                          title={ra.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-3">
                        <h4 className="text-xs md:text-sm font-semibold text-foreground-900 line-clamp-2 font-heading group-hover:text-primary-600 transition-colors">
                          {ra.title}
                        </h4>
                        <p className="text-xs text-foreground-500 mt-1">{ra.publishedAt}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}